import { useState, useEffect } from 'react';
import { Code, History, Settings, Sparkles } from 'lucide-react';
import { ImageUploader } from '../../components/feature/ImageUploader';
import { BeforeAfterSlider } from '../../components/feature/BeforeAfterSlider';
import { EmbedCodeModal } from '../../components/feature/EmbedCodeModal';
import { HistoryItem } from '../../components/feature/HistoryItem';
import { SettingsPanel } from '../../components/feature/SettingsPanel';
import { Button } from '../../components/base/Button';
import { SliderData, SliderSettings } from '../../types/slider';
import { saveSlider, getSliders, deleteSlider, getSettings, saveSettings } from '../../utils/storage';
import { fileToBase64, downloadImage, generateEmbedCode, convertGoogleDriveUrl } from '../../utils/imageUtils';
import toast, { Toaster } from 'react-hot-toast';

export default function HomePage() {
  const [beforeImage, setBeforeImage] = useState<string>('');
  const [afterImage, setAfterImage] = useState<string>('');
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [sliders, setSliders] = useState<SliderData[]>([]);
  const [settings, setSettings] = useState<SliderSettings>(getSettings());
  const [activeTab, setActiveTab] = useState<'upload' | 'history' | 'settings'>('upload');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadSliders();
  }, []);

  const loadSliders = async () => {
    try {
      const loadedSliders = await getSliders();
      setSliders(loadedSliders);
    } catch (error) {
      console.error('Failed to load sliders:', error);
      toast.error('履歴の読み込みに失敗しました');
    }
  };

  const handleBeforeImageSelect = async (file: File) => {
    const base64 = await fileToBase64(file);
    setBeforeImage(base64);
    toast.success('Before画像をアップロードしました');
  };

  const handleAfterImageSelect = async (file: File) => {
    const base64 = await fileToBase64(file);
    setAfterImage(base64);
    toast.success('After画像をアップロードしました');
  };

  const handleBeforeUrlInput = (url: string) => {
    const convertedUrl = convertGoogleDriveUrl(url);
    setBeforeImage(convertedUrl);
    
    // 画像の読み込みテスト
    const img = new Image();
    img.onload = () => {
      toast.success('Before画像URLを設定しました');
    };
    img.onerror = () => {
      toast.error('Before画像の読み込みに失敗しました。共有設定を確認してください', {
        duration: 5000,
      });
    };
    img.src = convertedUrl;
  };

  const handleAfterUrlInput = (url: string) => {
    const convertedUrl = convertGoogleDriveUrl(url);
    setAfterImage(convertedUrl);
    
    // 画像の読み込みテスト
    const img = new Image();
    img.onload = () => {
      toast.success('After画像URLを設定しました');
    };
    img.onerror = () => {
      toast.error('After画像の読み込みに失敗しました。共有設定を確認してください', {
        duration: 5000,
      });
    };
    img.src = convertedUrl;
  };

  const handleSaveSlider = async () => {
    if (!beforeImage || !afterImage) {
      toast.error('両方の画像をアップロードしてください');
      return;
    }

    setIsLoading(true);
    try {
      const newSlider: SliderData = {
        id: Date.now().toString(),
        beforeImage,
        afterImage,
        createdAt: Date.now()
      };

      await saveSlider(newSlider);
      await loadSliders();
      toast.success('スライダーを保存しました');
    } catch (error) {
      console.error('Failed to save slider:', error);
      toast.error('スライダーの保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateEmbed = () => {
    if (!beforeImage || !afterImage) {
      toast.error('両方の画像をアップロードしてください');
      return;
    }

    const code = generateEmbedCode(Date.now().toString(), beforeImage, afterImage);
    setEmbedCode(code);
    setShowEmbedModal(true);
  };

  const handlePreview = (slider: SliderData) => {
    setBeforeImage(slider.beforeImage);
    setAfterImage(slider.afterImage);
    setActiveTab('upload');
    toast.success('スライダーを読み込みました');
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('このスライダーを削除してもよろしいですか?')) {
      try {
        await deleteSlider(id);
        await loadSliders();
        toast.success('スライダーを削除しました');
      } catch (error) {
        console.error('Failed to delete slider:', error);
        toast.error('スライダーの削除に失敗しました');
      }
    }
  };

  const handleDownload = (slider: SliderData) => {
    downloadImage(slider.beforeImage, `before-${slider.id}.png`);
    downloadImage(slider.afterImage, `after-${slider.id}.png`);
    toast.success('画像をダウンロードしました');
  };

  const handleSaveSettings = (newSettings: SliderSettings) => {
    saveSettings(newSettings);
    setSettings(newSettings);
    toast.success('設定を保存しました');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center shadow-md">
                <i className="ri-image-2-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">画像比較スライダー生成ツール</h1>
                <p className="text-xs text-gray-500">Google Drive対応・軽量埋め込みコード</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={activeTab === 'upload' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('upload')}
                icon={<Sparkles className="w-4 h-4" />}
              >
                作成
              </Button>
              <Button
                variant={activeTab === 'history' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('history')}
                icon={<History className="w-4 h-4" />}
              >
                履歴
              </Button>
              <Button
                variant={activeTab === 'settings' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('settings')}
                icon={<Settings className="w-4 h-4" />}
              >
                設定
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'upload' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Upload */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-upload-cloud-line text-xl text-teal-600"></i>
                  </div>
                  画像アップロード
                </h2>
                
                <div className="space-y-6">
                  <ImageUploader
                    label="Before画像"
                    onImageSelect={handleBeforeImageSelect}
                    onUrlInput={handleBeforeUrlInput}
                    currentImage={beforeImage}
                  />
                  
                  <ImageUploader
                    label="After画像"
                    onImageSelect={handleAfterImageSelect}
                    onUrlInput={handleAfterUrlInput}
                    currentImage={afterImage}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">アクション</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="primary"
                    onClick={handleSaveSlider}
                    disabled={!beforeImage || !afterImage || isLoading}
                    icon={<i className="ri-save-line"></i>}
                  >
                    {isLoading ? '保存中...' : '保存'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleGenerateEmbed}
                    disabled={!beforeImage || !afterImage}
                    icon={<Code className="w-4 h-4" />}
                  >
                    埋め込みコード
                  </Button>
                </div>
              </div>

              {/* Google Drive Info */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-200 p-6">
                <h3 className="text-sm font-semibold text-teal-900 mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-google-line text-lg"></i>
                  </div>
                  Google Drive画像の使い方
                </h3>
                <ul className="text-sm text-teal-800 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">1.</span>
                    <span>Google Driveで画像を右クリック →「共有」</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">2.</span>
                    <span>「リンクを知っている全員」に変更</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">3.</span>
                    <span>リンクをコピーして、上の入力欄にペースト</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">4.</span>
                    <span>自動的に直リンクに変換されます</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-eye-line text-xl text-teal-600"></i>
                  </div>
                  ライブプレビュー
                </h2>
                
                {beforeImage && afterImage ? (
                  <BeforeAfterSlider
                    beforeImage={beforeImage}
                    afterImage={afterImage}
                    handleColor={settings.handleColor}
                    handleSize={settings.handleSize}
                    dividerWidth={settings.dividerWidth}
                  />
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <i className="ri-image-line text-6xl text-gray-300"></i>
                      </div>
                      <p className="text-gray-500 text-sm">
                        画像をアップロードするとプレビューが表示されます
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Info Card */}
              <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  使い方
                </h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">•</span>
                    <span>Before/After画像をアップロードまたはURLで指定</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">•</span>
                    <span>Google Driveリンクは自動的に直リンクに変換</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">•</span>
                    <span>スライダーをドラッグして比較確認</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5">•</span>
                    <span>軽量な埋め込みコードを生成してLPに設置</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <History className="w-5 h-5 text-teal-600" />
                スライダー履歴
              </h2>
              
              {sliders.length > 0 ? (
                <div className="space-y-4">
                  {sliders.map((slider) => (
                    <HistoryItem
                      key={slider.id}
                      slider={slider}
                      onPreview={handlePreview}
                      onDelete={handleDelete}
                      onDownload={handleDownload}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-folder-open-line text-6xl text-gray-300"></i>
                  </div>
                  <p className="text-gray-500">まだスライダーが作成されていません</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setActiveTab('upload')}
                    className="mt-4"
                  >
                    最初のスライダーを作成
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto">
            <SettingsPanel settings={settings} onSave={handleSaveSettings} />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              © 2025 画像比較スライダー生成ツール. All rights reserved.
            </p>
            <div className="mt-4">
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Embed Modal */}
      {showEmbedModal && (
        <EmbedCodeModal
          embedCode={embedCode}
          onClose={() => setShowEmbedModal(false)}
        />
      )}
    </div>
  );
}
